import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowPathIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  ClockIcon,
  UserIcon,
  ShieldCheckIcon,
  NoSymbolIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import AdminLayout from '../components/AdminLayout';
import Pagination from '../components/Pagination';
import toast from 'react-hot-toast';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Action type configurations
const ACTION_TYPES = {
  captcha_required: { icon: ShieldCheckIcon, color: 'bg-blue-100 text-blue-700', label: 'Captcha Required' },
  captcha_passed: { icon: ShieldCheckIcon, color: 'bg-green-100 text-green-700', label: 'Captcha Passed' },
  captcha_failed: { icon: ShieldCheckIcon, color: 'bg-red-100 text-red-700', label: 'Captcha Failed' },
  otp_required: { icon: ShieldCheckIcon, color: 'bg-purple-100 text-purple-700', label: 'OTP Required' },
  otp_sent: { icon: ShieldCheckIcon, color: 'bg-purple-100 text-purple-700', label: 'OTP Sent' },
  otp_verified: { icon: ShieldCheckIcon, color: 'bg-green-100 text-green-700', label: 'OTP Verified' },
  otp_failed: { icon: ShieldCheckIcon, color: 'bg-red-100 text-red-700', label: 'OTP Failed' },
  blocked: { icon: NoSymbolIcon, color: 'bg-red-100 text-red-700', label: 'Blocked' },
  unblocked: { icon: NoSymbolIcon, color: 'bg-green-100 text-green-700', label: 'Unblocked' },
  flagged: { icon: ExclamationTriangleIcon, color: 'bg-yellow-100 text-yellow-700', label: 'Flagged' },
  unflagged: { icon: ExclamationTriangleIcon, color: 'bg-gray-100 text-gray-700', label: 'Unflagged' },
  risk_score_updated: { icon: DocumentTextIcon, color: 'bg-gray-100 text-gray-700', label: 'Risk Updated' },
  manual_review: { icon: UserIcon, color: 'bg-blue-100 text-blue-700', label: 'Manual Review' },
  auto_blocked: { icon: NoSymbolIcon, color: 'bg-red-100 text-red-700', label: 'Auto Blocked' },
  login_attempt: { icon: UserIcon, color: 'bg-gray-100 text-gray-600', label: 'Login Attempt' },
  generation_attempt: { icon: DocumentTextIcon, color: 'bg-gray-100 text-gray-600', label: 'Generation' },
  signup_attempt: { icon: UserIcon, color: 'bg-blue-100 text-blue-600', label: 'Signup' }
};

// Action badge component
const ActionBadge = ({ type }) => {
  const config = ACTION_TYPES[type] || { icon: ClockIcon, color: 'bg-gray-100 text-gray-700', label: type };
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded ${config.color}`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
};

const RiskAudit = () => {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalActions, setTotalActions] = useState(0);
  const [actionType, setActionType] = useState('');
  const [userId, setUserId] = useState('');
  const [performedBy, setPerformedBy] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showFilters, setShowFilters] = useState(false);

  const fetchAuditLog = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: 50,
        sortOrder,
        ...(actionType && { actionType }),
        ...(userId && { userId }),
        ...(performedBy && { performedBy }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate })
      });

      const response = await axios.get(`${API_BASE_URL}/api/admin/risk/audit?${params}`);
      setActions(response.data.data.actions);
      setTotalPages(response.data.data.pagination.totalPages);
      setTotalActions(response.data.data.pagination.total);
    } catch (error) {
      console.error('Error fetching audit log:', error);
      toast.error('Failed to fetch audit log');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAuditLog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, actionType, sortOrder, startDate, endDate]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchAuditLog();
  };

  const handleExport = async () => {
    try {
      // Build CSV content
      const headers = ['Timestamp', 'Action Type', 'User', 'Reason', 'Performed By', 'IP Address', 'Risk Score'];
      const rows = actions.map(action => [
        new Date(action.created_at).toISOString(),
        action.action_type,
        action.target_user_email || 'N/A',
        action.action_reason || '',
        action.performed_by_email || 'System',
        action.ip_address || '',
        action.risk_score_at_action || ''
      ]);
      
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      ].join('\n');
      
      // Download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `risk-audit-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Audit log exported');
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Failed to export');
    }
  };

  const clearFilters = () => {
    setActionType('');
    setUserId('');
    setPerformedBy('');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
  };

  return (
    <AdminLayout>
      <div>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Audit Trail</h1>
            <p className="text-gray-600 mt-1">
              {totalActions.toLocaleString()} total actions recorded
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/risk"
              className="px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
            >
              ← Back to Dashboard
            </Link>
            <button
              onClick={handleExport}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2"
            >
              <ArrowDownTrayIcon className="w-5 h-5" />
              Export CSV
            </button>
            <button
              onClick={fetchAuditLog}
              disabled={loading}
              className="p-2 text-gray-600 hover:text-primary-600 hover:bg-gray-100 rounded-lg"
            >
              <ArrowPathIcon className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-2 rounded-lg border flex items-center gap-2 ${
                showFilters ? 'bg-primary-50 border-primary-300 text-primary-700' : 'border-gray-300 text-gray-600'
              }`}
            >
              <FunnelIcon className="w-5 h-5" />
              Filters
              {(actionType || userId || performedBy || startDate || endDate) && (
                <span className="w-2 h-2 bg-primary-500 rounded-full"></span>
              )}
            </button>
            
            {/* Quick action type filter */}
            <select
              value={actionType}
              onChange={(e) => { setActionType(e.target.value); setCurrentPage(1); }}
              className="border border-gray-300 rounded-lg p-2"
            >
              <option value="">All Actions</option>
              {Object.entries(ACTION_TYPES).map(([key, { label }]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
            
            {/* Sort order */}
            <select
              value={sortOrder}
              onChange={(e) => { setSortOrder(e.target.value); setCurrentPage(1); }}
              className="border border-gray-300 rounded-lg p-2"
            >
              <option value="desc">Newest First</option>
              <option value="asc">Oldest First</option>
            </select>
            
            {(actionType || userId || performedBy || startDate || endDate) && (
              <button
                onClick={clearFilters}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Clear filters
              </button>
            )}
          </div>

          {showFilters && (
            <div className="mt-4 pt-4 border-t grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">User ID</label>
                <input
                  type="text"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  placeholder="Filter by user ID"
                  className="w-full border border-gray-300 rounded-lg p-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Performed By</label>
                <input
                  type="text"
                  value={performedBy}
                  onChange={(e) => setPerformedBy(e.target.value)}
                  placeholder="Filter by admin ID"
                  className="w-full border border-gray-300 rounded-lg p-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="datetime-local"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg p-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input
                  type="datetime-local"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg p-2"
                />
              </div>
              <div className="md:col-span-4">
                <button
                  onClick={handleSearch}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Audit Log Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Timestamp</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Target User</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Performed By</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Risk Score</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                    </div>
                  </td>
                </tr>
              ) : actions.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                    No audit records found
                  </td>
                </tr>
              ) : (
                actions.map((action) => (
                  <tr key={action.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <ClockIcon className="w-4 h-4 text-gray-400" />
                        <div>
                          <p className="text-sm text-gray-900">
                            {new Date(action.created_at).toLocaleDateString()}
                          </p>
                          <p className="text-xs text-gray-500">
                            {new Date(action.created_at).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <ActionBadge type={action.action_type} />
                    </td>
                    <td className="px-4 py-3">
                      {action.user_id ? (
                        <Link
                          to={`/risk/users/${action.user_id}`}
                          className="text-sm text-primary-600 hover:text-primary-700"
                        >
                          {action.target_user_email || action.target_user_name || 'View User'}
                        </Link>
                      ) : (
                        <span className="text-sm text-gray-400">N/A</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-gray-600 max-w-xs truncate" title={action.action_reason}>
                        {action.action_reason || '-'}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      {action.is_system_action ? (
                        <span className="text-sm text-gray-500 italic">System</span>
                      ) : action.performed_by ? (
                        <span className="text-sm text-gray-700">
                          {action.performed_by_email || action.performed_by_name || 'Admin'}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {action.risk_score_at_action !== null ? (
                        <span className={`text-sm font-medium ${
                          action.risk_score_at_action >= 60 ? 'text-red-600' :
                          action.risk_score_at_action >= 30 ? 'text-yellow-600' :
                          'text-green-600'
                        }`}>
                          {action.risk_score_at_action}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-500">
                        {action.ip_address || '-'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-4">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default RiskAudit;
